import { apiClient } from "@api/client";
import { request, requestVoid } from "@api/request";
import type { components } from "@api/schema";
import { ClinicAuthError } from "@auth/clinicAuthClient";

/** Mirrors brian `models/room.py` `AudioGateMode` and Zuul `audio/policy.py` `Mode`; null = Zuul's default. */
export type AudioGateMode = components["schemas"]["RoomOut"]["audio_gate_mode"];

export type CameraTransport =
  components["schemas"]["RoomOut"]["camera_transport"];

/** Mirrors brian `models/room.py` `RoomKind`: it sets the phases perception gives the room; `none` gets no phase. */
export type RoomKind = components["schemas"]["RoomKind"];

export type RoomChange = components["schemas"]["RoomUpdate"];

export const ROOM_KIND_LABEL: Record<RoomKind, string> = {
  exam: "Exam room",
  reception: "Reception",
  treatment: "Treatment",
  none: "No phases",
};

/** `[x1, y1, x2, y2]` in the camera's source pixels; mirrors brian `RoomCameraSet.fixtures`. */
export type FixtureBox = readonly [number, number, number, number];

export interface Room {
  id: string;
  name: string;
  kind: RoomKind;
  active: boolean;
  captureEnabled: boolean | null;
  captureRevision: number;
  audioGateMode: AudioGateMode;
  /** Adaptive margin in dB above the learned floor; null = Zuul's env default (12). */
  audioGateMarginDb: number | null;
  audioGateRevision: number;
  cameraTransport: CameraTransport;
  /** The empty-room mask; null is off. Applies to every camera in the room. */
  cameraFixtures: FixtureBox[] | null;
  /** When the empty-room baseline was saved; null when the room has none. */
  cameraBaselineAt: string | null;
}

/** Mirrors brian `RoomBaselineOut`; `url` is presigned and short-lived. */
interface RoomBaseline {
  url: string;
  capturedAt: string;
}

const toBaseline = (
  b: components["schemas"]["RoomBaselineOut"],
): RoomBaseline => ({
  url: b.url,
  capturedAt: b.captured_at,
});

/** brian validates each box as four numbers on PUT, so a saved one always has four. */
export const toFixture = ([
  x1 = 0,
  y1 = 0,
  x2 = 0,
  y2 = 0,
]: number[]): FixtureBox => [x1, y1, x2, y2] as const;

const toFixtures = (saved: number[][] | null): FixtureBox[] | null =>
  saved?.map(toFixture) ?? null;

const toRoom = (r: components["schemas"]["RoomOut"]): Room => ({
  id: r.uuid,
  name: r.name,
  kind: r.kind,
  active: r.active,
  captureEnabled: r.capture_enabled,
  captureRevision: r.capture_revision,
  audioGateMode: r.audio_gate_mode,
  audioGateMarginDb: r.audio_gate_margin_db,
  audioGateRevision: r.audio_gate_revision,
  cameraTransport: r.camera_transport,
  cameraFixtures: toFixtures(r.camera_fixtures),
  cameraBaselineAt: r.camera_baseline_at,
});

export const roomsClient = {
  list: (opts?: { active?: boolean }): Promise<Room[]> =>
    request(() =>
      apiClient.GET("/rooms", {
        params: {
          query: opts?.active === undefined ? {} : { active: opts.active },
        },
      }),
    ).then((rows) => rows.map(toRoom)),

  /** 409 `visit_not_in_room` when the visit has left the room since it was read.
   * `includeHousehold` also confirms the household's other visits in the room (BRI-377). */
  confirmVisit: ({
    roomUuid,
    visitUuid,
    includeHousehold,
  }: {
    roomUuid: string;
    visitUuid: string;
    includeHousehold: boolean;
  }) =>
    requestVoid(() =>
      apiClient.POST("/rooms/{room_uuid}/confirmations", {
        params: { path: { room_uuid: roomUuid } },
        body: { visit_uuid: visitUuid, include_household: includeHousehold },
      }),
    ),

  releaseVisit: (roomUuid: string, visitUuid: string) =>
    requestVoid(() =>
      apiClient.POST("/rooms/{room_uuid}/confirmations/{visit_uuid}/release", {
        params: { path: { room_uuid: roomUuid, visit_uuid: visitUuid } },
      }),
    ),

  /** Leadership only; 409 `room_name_taken`. */
  create: (name: string, kind: RoomKind) =>
    request(() =>
      apiClient.POST("/rooms", { body: { name, kind, active: true } }),
    ).then(toRoom),

  /** Leadership only; 409 `room_name_taken` on a rename. */
  update: (roomUuid: string, change: RoomChange) =>
    request(() =>
      apiClient.PATCH("/rooms/{room_uuid}", {
        params: { path: { room_uuid: roomUuid } },
        body: change,
      }),
    ).then(toRoom),

  /** Leadership only (brian `LeadershipAuth`). The PUT replaces the whole config, so send the transport unchanged. */
  setCamera: (
    roomUuid: string,
    transport: CameraTransport,
    fixtures: readonly FixtureBox[] | null,
  ) =>
    request(() =>
      apiClient.PUT("/rooms/{room_uuid}/camera", {
        params: { path: { room_uuid: roomUuid } },
        body: {
          transport,
          fixtures: fixtures?.map((box) => [...box]) ?? null,
        },
      }),
    ),

  /** Leadership only. Null when the room has no baseline (404 `no_baseline`). */
  baseline: (roomUuid: string): Promise<RoomBaseline | null> =>
    request(() =>
      apiClient.GET("/rooms/{room_uuid}/camera/baseline", {
        params: { path: { room_uuid: roomUuid } },
      }),
    ).then(toBaseline, (error: unknown) => {
      if (error instanceof ClinicAuthError && error.status === 404) return null;
      throw error;
    }),

  /** Leadership only. A null reference saves the room's newest still; brian refuses a room with several cameras (409). */
  setBaseline: (roomUuid: string, stillReference: string | null) =>
    request(() =>
      apiClient.PUT("/rooms/{room_uuid}/camera/baseline", {
        params: { path: { room_uuid: roomUuid } },
        body: { still_reference: stillReference },
      }),
    ).then(toBaseline),

  clearBaseline: (roomUuid: string) =>
    requestVoid(() =>
      apiClient.DELETE("/rooms/{room_uuid}/camera/baseline", {
        params: { path: { room_uuid: roomUuid } },
      }),
    ),
};
